import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"
import { Turbo } from "@hotwired/turbo-rails"

// Starts an AI generation. The controller answers with a turbo stream showing the
// loading state (rendered by FetchRequest), then the job refreshes the page once
// the content is ready. Any other outcome is surfaced instead of ignored.
export default class extends Controller {
  static values = { url: String, errorMessage: String }
  static targets = ["dateFrom", "dateTo", "category", "dateRangePicker"]

  async generate(event) {
    event.preventDefault()

    const button = event.currentTarget
    button.disabled = true
    this.clearError()

    const request = new FetchRequest("post", this.urlValue, { body: this.params(), responseKind: "turbo-stream" })

    try {
      const response = await request.perform()

      if (response.redirected) {
        Turbo.visit(response.response.url)
      } else if (response.unauthenticated) {
        Turbo.visit(window.location.href) // lets Devise send the teacher to sign in
      } else if (!response.ok) {
        this.showError(button)
      }
    } catch {
      this.showError(button)
    }
  }

  params() {
    const params = new FormData()

    if (this.hasDateRangePickerTarget) {
      const dateFrom = this.dateRangePickerTarget.dataset.dateFrom
      const dateTo = this.dateRangePickerTarget.dataset.dateTo
      if (dateFrom) params.append("date_from", dateFrom)
      if (dateTo) params.append("date_to", dateTo)
    } else {
      if (this.hasDateFromTarget) params.append("date_from", this.dateFromTarget.value)
      if (this.hasDateToTarget) params.append("date_to", this.dateToTarget.value)
    }

    if (this.hasCategoryTarget && this.categoryTarget.value) params.append("category", this.categoryTarget.value)

    return params
  }

  showError(button) {
    button.disabled = false

    const alert = document.createElement("div")
    alert.className = "o-alert o-alert--danger mt-2"
    alert.dataset.aiGenerateError = ""
    const content = document.createElement("div")
    content.className = "o-alert__content"
    content.textContent = this.errorMessageValue
    alert.append(content)
    this.element.append(alert)
  }

  clearError() {
    const alert = this.element.querySelector("[data-ai-generate-error]")
    if (alert) alert.remove()
  }

  toggleDatePicker(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasDateRangePickerTarget) return

    const calendar = this.dateRangePickerTarget
    const isOpen = calendar.classList.contains("open")

    if (isOpen) {
      calendar.classList.remove("open")
      document.removeEventListener("click", this.boundClosePicker)
    } else {
      calendar.classList.add("open")

      const pickerController = this.application.getControllerForElementAndIdentifier(
        calendar, "date-range-picker"
      )
      if (pickerController) pickerController.open()

      this.boundClosePicker = (e) => {
        if (!this.element.contains(e.target)) {
          calendar.classList.remove("open")
          document.removeEventListener("click", this.boundClosePicker)
        }
      }
      document.addEventListener("click", this.boundClosePicker)
    }
  }

  applyDateRange(event) {
    event.preventDefault()
    if (!this.hasDateRangePickerTarget) return

    this.dateRangePickerTarget.classList.remove("open")
    document.removeEventListener("click", this.boundClosePicker)
  }

  clearDateRange(event) {
    event.preventDefault()
    if (!this.hasDateRangePickerTarget) return

    const pickerController = this.application.getControllerForElementAndIdentifier(
      this.dateRangePickerTarget, "date-range-picker"
    )
    if (pickerController) pickerController.clear()

    this.dateRangePickerTarget.classList.remove("open")
    document.removeEventListener("click", this.boundClosePicker)
  }
}
